import React, { useEffect } from 'react'
import mirador from 'mirador'

import getAnnotations from '../api/drupal'
import { IAnnoPage } from '../types/IAnnoPage'

interface IProps {
  config: {
    id: string
    manifest: string
  }

  plugins: IPlugin[]
}

interface IPlugin {
  name: string
  target: string
  mode: string
  component: React.FC
}

const Mirador: React.FC<IProps> = ({ config, plugins }) => {
  useEffect(() => {
    const { store } = mirador.viewer(
      {
        annotations: {
          htmlSanitizationRuleSet: 'liberal',
        },
        id: config.id,
        // configured from https://github.com/ProjectMirador/mirador/blob/master/src/config/settings.js
        selectedTheme: 'dark',
        theme: {
          palette: {
            annotations: {
              default: { strokeStyle: '#4a4a4a', globalAlpha: 1 },
              hovered: { strokeStyle: '#222222', globalAlpha: 1 },
              selected: { strokeStyle: '#286dc0', globalAlpha: 1 },
            },
          },
          typography: {
            body2: {
              fontSize: '1rem',
              letterSpacing: '0em',
            },
          },
        },
        window: {
          allowClose: false, // Configure if windows can be closed or not
          allowFullscreen: true, // Configure to show a "fullscreen" button in the WindowTopBar
          allowMaximize: false, // Configure if windows can be maximized or not
          allowTopMenuButton: false, // Configure if window view and thumbnail display menu are visible or not
          allowWindowSideBar: false, // Configure if side bar menu is visible or not
          defaultView: 'scroll', // Configure which viewing mode (e.g. single, book, gallery) for windows to be opened in
          defaultSidebarPanelWidth: 224,
          forceDrawAnnotations: true,
          hideWindowTitle: false, // Configure if the window title is shown in the window title bar or not
          highlightAllAnnotations: true, // Configure whether to display annotations on the canvas by default
        },
        windows: [
          {
            id: 'main_scroll',
            manifestId: `${process.env.REACT_APP_MANIFEST_URL}/${config.manifest}.json`,
          },
        ],
        workspaceControlPanel: {
          enabled: false, // Configure if the control panel should be rendered.  Useful if you want to lock the viewer down to only the configured manifests
        },
      },
      plugins,
    )

    store.dispatch({
      id: 'Transcription', // This id must match the tag in the manifest exactly
      windowId: 'main_scroll',
      payload: {
        title: 'Transcription',
        default: true,
        content: 'annotations',
        position: 'right',
      },
      type: 'mirador/ADD_COMPANION_WINDOW',
    })

    store.dispatch({
      id: 'English', // This id must match the tag in the manifest exactly
      windowId: 'main_scroll',
      payload: {
        title: 'Modern English',
        default: true,
        content: 'annotations',
        position: 'bottom',
      },
      type: 'mirador/ADD_COMPANION_WINDOW',
    })

    store.dispatch({
      id: 'Japanese', // This id must match the tag in the manifest exactly
      windowId: 'main_scroll',
      payload: {
        title: 'Modern Japanese',
        default: true,
        content: 'annotations',
        position: 'bottom',
      },
      type: 'mirador/ADD_COMPANION_WINDOW',
    })

    store.dispatch({
      id: 'Image', // This id must match the tag in the manifest exactly
      windowId: 'main_scroll',
      payload: {
        title: 'Image Annotation',
        default: true,
        content: 'annotations',
        position: 'right',
      },
      type: 'mirador/ADD_COMPANION_WINDOW',
    })

    getAnnotations(config.manifest).then((annotations: IAnnoPage) => {
      try {
        store.dispatch({
          annotationId: 'site_url/content/page/1',
          annotationJson: annotations,
          targetId: 'site_url/manifests/1/canvases/1',
          type: 'mirador/RECEIVE_ANNOTATION',
        })
      } catch (e) {}
    })

    // Scroll to the start of the Kyoto Komonjo scroll if we are looking at it
    if (config.manifest === '32') {
      setTimeout(() => {
        store.dispatch({
          payload: {
            x: 95000,
            zoom: 0.0001,
          },
          type: 'mirador/UPDATE_VIEWPORT',
          windowId: 'main_scroll',
        })
      }, 1000)
    }
  })

  return <div id={config.id} />
}

export default Mirador
